import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import api from '../services/api';
import { formatDateTime } from '../utils/dates';
import { fieldErrors, wranglerError } from './errors';
import ImportResultView from './ImportResultView';
import useWranglerJob, { responsesProgressText } from './useWranglerJob';

const JOB_LABELS = {
  sync_questions: 'Question sync',
  refresh: 'Refresh',
  import_alchemer_quotas: 'Import from Alchemer',
};
const JOB_COLOURS = { queued: 'default', running: 'info', done: 'success', failed: 'error' };
const RECENT_JOBS = 5;

const HealthChip = () => {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    api.get('/surveys/wrangler-health/')
      .then((res) => setHealth(res.data))
      .catch(() => setHealth({ configured: true, responding: false }));
  }, []);

  if (!health) return null;
  if (!health.configured) return <Chip size="small" label="Not set up on this server" />;
  return health.responding
    ? <Chip size="small" color="success" label="Responding" />
    : <Chip size="small" color="error" label="Not responding" />;
};

const SyncResult = ({ job, error }) => {
  if (error) return <Alert severity="warning">Couldn't follow the question sync: {error}</Alert>;
  if (!job) return null;
  if (job.status === 'failed') {
    return <Alert severity="error">The question sync failed: {job.error || 'no reason given.'}</Alert>;
  }
  if (job.status !== 'done') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Syncing questions from Alchemer…</Typography>
      </Box>
    );
  }
  const result = job.progress?.result;
  if (!result) return <Alert severity="success">Questions synced.</Alert>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Alert severity="success">
        Synced {result.questions} questions: {result.added.length} new, {result.removed.length} removed.
      </Alert>
      {result.used_but_missing.length > 0 && (
        <Alert severity="error">
          Quotas or rules use questions Alchemer no longer has: {result.used_but_missing.join(', ')}.
          Fix those quotas or rules; refreshes fail until then.
        </Alert>
      )}
      {result.used_with_duplicate_reporting_values.length > 0 && (
        <Alert severity="warning">
          Questions used by quotas or rules now have duplicate reporting values:{' '}
          {result.used_with_duplicate_reporting_values.join(', ')}.
        </Alert>
      )}
    </Box>
  );
};

const RESULT_LINES = [
  ['new_count', 'new respondents'],
  ['newly_complete_count', 'known respondents now complete'],
  ['reinstated_count', 'removed earlier, complete again'],
  ['removed_count', 'no longer Complete in Alchemer, removed'],
  ['kept_recent_completes_count', 'missing from Alchemer but recent, kept for now'],
  ['duplicate_session_ids_count', 'session IDs on more than one response (the later one kept)'],
  ['responses_without_session_id_count', 'responses without a session ID, skipped'],
  ['answer_problems_count', "answers that couldn't be read"],
];

const RefreshResult = ({ job, error }) => {
  if (error) return <Alert severity="warning">Couldn't follow the refresh: {error}</Alert>;
  if (!job) return null;
  if (job.status === 'failed') return <Alert severity="error">The refresh failed: {job.error || 'no reason given.'}</Alert>;
  if (job.status !== 'done') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Refreshing from Alchemer… {responsesProgressText(job.progress)}</Typography>
      </Box>
    );
  }
  const r = job.progress?.result;
  if (!r) return <Alert severity="success">Refreshed.</Alert>;
  const lines = RESULT_LINES.filter(([key]) => r[key]);
  const stillComplete = r.disqualified_but_complete_in_alchemer || [];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Alert severity="success">
        Refreshed: {r.counted} completes counted ({r.fetched} read from Alchemer); {r.quotas_recounted} quota
        {r.quotas_recounted !== 1 ? 's' : ''} changed count.
        {lines.length > 0 && (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {lines.map(([key, text]) => <li key={key}>{r[key]} {text}</li>)}
          </Box>
        )}
      </Alert>
      {r.disqualified_but_complete_in_alchemer_count > 0 && (
        <Alert severity="warning">
          {r.disqualified_but_complete_in_alchemer_count} respondent
          {r.disqualified_but_complete_in_alchemer_count !== 1 ? 's' : ''} disqualified by Ranger
          {r.disqualified_but_complete_in_alchemer_count !== 1 ? ' are' : ' is'} still Complete in Alchemer. They stay
          disqualified in Wrangler; check why Alchemer still lists them.
          <Box sx={{ fontFamily: 'monospace', fontSize: 12, mt: 0.5 }}>
            {stillComplete.map((x) => `${x.session_id} (response ${x.alchemer_response_id})`).join(', ')}
            {r.disqualified_but_complete_in_alchemer_count > stillComplete.length && ' …'}
          </Box>
        </Alert>
      )}
    </Box>
  );
};

const Counts = ({ counts }) => (
  <Typography variant="body2">
    <strong>{counts.complete}</strong> complete · <strong>{counts.in_progress}</strong> in progress
    {' '}(<strong>{counts.live_reservations}</strong> holding a place) ·{' '}
    <strong>{counts.disqualified_in_survey}</strong> disqualified in the survey ·{' '}
    <strong>{counts.disqualified_after}</strong> disqualified by Ranger ·{' '}
    <strong>{counts.removed}</strong> removed
  </Typography>
);

const RecentJobs = ({ jobs, onOpenResult }) => {
  if (!jobs.length) return <Typography variant="body2" color="textSecondary">No jobs yet.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Job</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Queued</TableCell>
          <TableCell>Finished</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {jobs.map((job) => (
          <TableRow key={job.id}>
            <TableCell>{JOB_LABELS[job.kind] || job.kind}</TableCell>
            <TableCell><Chip size="small" label={job.status} color={JOB_COLOURS[job.status] || 'default'} /></TableCell>
            <TableCell>{formatDateTime(job.created_at)}</TableCell>
            <TableCell>{formatDateTime(job.finished_at)}</TableCell>
            <TableCell>
              {job.kind === 'import_alchemer_quotas' && job.status === 'done' && (
                <Button size="small" onClick={() => onOpenResult(job)} sx={{ textTransform: 'none' }}>View result</Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * The survey in Wrangler, on the survey's Wrangler page: whether it's
 * registered (and if not, the offer to set it up), Wrangler's own counts,
 * the question sync, refresh, webhook setup and recent jobs. Its errors are
 * its own: Wrangler being down never hides the rest of the page.
 */
const WranglerSection = ({ survey, onStatus }) => {
  const surveyId = survey.id;
  const [resultJob, setResultJob] = useState(null);
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [jobs, setJobs] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [syncJobId, setSyncJobId] = useState(null);
  const { job: syncJob, error: syncError } = useWranglerJob(surveyId, syncJobId);
  const [refreshJobId, setRefreshJobId] = useState(null);
  const { job: refreshJob, error: refreshError } = useWranglerJob(surveyId, refreshJobId);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(`/surveys/${surveyId}/wrangler/`);
      setState({ loading: false, error: null, data });
      onStatus?.(data);
      if (data.registered) {
        const res = await api.get(`/surveys/${surveyId}/wrangler/jobs/`);
        setJobs(res.data.results.slice(0, RECENT_JOBS));
      }
    } catch (err) {
      setState({ loading: false, error: wranglerError(err), data: null });
    }
    // onStatus tells the page about registration; it doesn't change what to load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  useEffect(() => { load(); }, [load]);

  // A finished job changes the counts, questions_synced_at and the job list.
  const syncStatus = syncJob?.status;
  const refreshStatus = refreshJob?.status;
  useEffect(() => {
    if (['done', 'failed'].includes(syncStatus) || ['done', 'failed'].includes(refreshStatus)) load();
  }, [syncStatus, refreshStatus, load]);

  const showActionError = (err) => {
    const fields = fieldErrors(err);
    setActionError([wranglerError(err), ...fields].join('\n'));
  };

  const register = async () => {
    setConfirming(false);
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const { data } = await api.post(`/surveys/${surveyId}/wrangler/register/`);
      if (data.already_registered) {
        setNotice('This survey was already registered in Wrangler. Nothing was changed.');
      } else if (data.sync) {
        setSyncJobId(data.sync.job.id);
      } else {
        setNotice("Registered, but the question sync couldn't be queued. Use “Sync questions”.");
      }
      await load();
    } catch (err) {
      showActionError(err);
    } finally {
      setBusy(false);
    }
  };

  const syncQuestions = async () => {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const { data } = await api.post(`/surveys/${surveyId}/wrangler/sync-questions/`);
      setSyncJobId(data.job.id);
    } catch (err) {
      showActionError(err);
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const { data } = await api.post(`/surveys/${surveyId}/wrangler/refresh/`);
      setRefreshJobId(data.job.id);
      if (!data.queued) setNotice('A refresh was already waiting to run; following that one.');
    } catch (err) {
      showActionError(err);
    } finally {
      setBusy(false);
    }
  };

  const syncing = Boolean(syncJobId) && !syncError && !['done', 'failed'].includes(syncStatus);
  const refreshing = Boolean(refreshJobId) && !refreshError && !['done', 'failed'].includes(refreshStatus);
  const wrangler = state.data?.survey;

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography variant="h6" fontFamily="'Work Sans', sans-serif" fontWeight={600}>
          Survey status
        </Typography>
        <HealthChip />
      </Box>

      {state.loading && !state.data && <CircularProgress size={20} />}

      {state.error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
          {state.error}
        </Alert>
      )}

      {state.data && !state.data.registered && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
          <Typography variant="body2">
            This survey isn't set up in Wrangler yet. Setting it up registers it and syncs its questions from
            Alchemer; it doesn't change the survey's Alchemer quotas.
          </Typography>
          <Button variant="contained" disabled={busy} onClick={() => setConfirming(true)}
            sx={{ textTransform: 'none', borderRadius: 2 }}>
            Set up in Wrangler
          </Button>
        </Box>
      )}

      {wrangler && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {wrangler.name !== survey.name && (
            <Alert severity="warning">
              Wrangler has Alchemer ID {wrangler.alchemer_survey_id} under the name
              {' '}<strong>{wrangler.name}</strong>. Check it's the same survey as <strong>{survey.name}</strong>.
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip size="small" label={wrangler.is_active ? 'Active' : 'Inactive'}
              color={wrangler.is_active ? 'success' : 'default'} />
            <Typography variant="body2" color="textSecondary">
              Questions synced: {wrangler.questions_synced_at ? formatDateTime(wrangler.questions_synced_at) : 'never'}
            </Typography>
          </Box>
          <Counts counts={wrangler.respondent_counts} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" disabled={busy || syncing} onClick={syncQuestions}
              sx={{ textTransform: 'none' }}>
              Sync questions
            </Button>
            <Tooltip title="Recounts every quota from Alchemer's Complete responses. It holds the survey while it runs, so checks can answer 'unchecked': run it away from peak fieldwork.">
              <span>
                <Button variant="outlined" size="small" disabled={busy || refreshing} onClick={refresh}
                  sx={{ textTransform: 'none' }}>
                  Refresh from Alchemer
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" size="small" component={Link} to={`/survey/${surveyId}/wrangler-setup`}
              sx={{ textTransform: 'none' }}>
              Alchemer webhook setup
            </Button>
          </Box>
          <Typography variant="caption" color="textSecondary">
            Run refreshes away from peak fieldwork: while one runs, respondents may not be quota-checked.
          </Typography>
          <SyncResult job={syncJob} error={syncError} />
          <RefreshResult job={refreshJob} error={refreshError} />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Recent jobs</Typography>
          <RecentJobs jobs={jobs} onOpenResult={setResultJob} />
          <Dialog open={Boolean(resultJob)} onClose={() => setResultJob(null)} maxWidth="md" fullWidth>
            <DialogTitle>Import from Alchemer, {resultJob && formatDateTime(resultJob.finished_at)}</DialogTitle>
            <DialogContent><ImportResultView result={resultJob?.progress?.result} /></DialogContent>
            <DialogActions>
              <Button onClick={() => setResultJob(null)} sx={{ textTransform: 'none' }}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {notice && <Alert severity="info" sx={{ mt: 2 }}>{notice}</Alert>}
      {actionError && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{actionError}</Alert>}

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Register in Wrangler?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Register <strong>{survey.name}</strong> with Alchemer ID <strong>{survey.alchemer_survey_id}</strong>.
            Check the ID: Wrangler counts this survey's responses under it. Its questions are then synced from Alchemer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={register} sx={{ textTransform: 'none' }}>Register</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default WranglerSection;
